import type { Direction, Modality } from '../contracts';
import { nextProgressionLevel, stakeForLevel, type Outcome, type ProgressionConfig } from '../progression';
import { ROUND_CLOCKS, roundKind, type SideResult } from '../rounds';
import type { AnyStrategy, MarketTick } from './types';

/** One finished round, as the backtest reads it. */
export interface BacktestRound {
  id: number;
  /** Closing epoch (seconds). */
  endAt: number;
  digit: number;
  move: 'UP' | 'DOWN' | 'FLAT' | null;
  /** Rush: the round flew to the cash-out being tested (an entry on it won). `move` is its candle color. */
  hit?: boolean;
  /** Rush: ticks the round flew (its % on the history chip), so candles like the 1.000%+ dourada can be matched. */
  ticks?: number;
  /**
   * A played round whose sides the broker opened at different prices (see RoundReference): what each side
   * paid. Only then can one side lose while the other is returned (or both lose), so the move can't tell.
   */
  paid?: { rise: SideResult; fall: SideResult };
}

export interface BacktestInput {
  strategy: AnyStrategy;
  params: unknown;
  modality: Modality;
  /** Oldest first, consecutive rounds of one clock. */
  rounds: readonly BacktestRound[];
  baseStake: number;
  progression: ProgressionConfig;
  /** Net return of a win, e.g. 0.89 (profit = stake × payoutRate). */
  payoutRate: number;
  /** Rounds the live robot keeps in its window. */
  windowSize?: number;
}

export interface BacktestEntry {
  round: number;
  at: number;
  direction: Direction;
  level: number;
  stake: number;
  outcome: Outcome;
  profit: number;
}

export interface BacktestResult {
  rounds: number;
  hours: number;
  entries: number;
  wins: number;
  losses: number;
  /** Chains (first entry + its gales). */
  chains: number;
  chainWins: number;
  chainLosses: number;
  /** Chains won at each level: [first entry, gale 1, …]. */
  winsByLevel: number[];
  net: number;
  /** Deepest fall from a running high, as a positive number. */
  maxDrawdown: number;
  maxLossStreak: number;
  /** Most rounds in a row without an entry. */
  longestWait: number;
  /** Running result after each entry, starting at 0. */
  curve: number[];
  /** Newest last. */
  entriesLog: BacktestEntry[];
  /** Session the strategy itself would have ended (cycle stops), if any. */
  endedBy: string | null;
}

/**
 * What an entry on this round paid, as Deriv settles it: Dígitos by the parity of the closing digit;
 * Sobe e Desce by the move, where a level close (FLAT) loses both sides (Rise/Fall wins only strictly
 * above / below the entry). A round read per side pays per side (an entry the broker returned is
 * NEUTRAL: the gale keeps its level). A round without a reading counts as NEUTRAL (nothing to settle).
 */
export function roundOutcome(modality: Modality, direction: Direction, r: BacktestRound): Outcome {
  if (r.paid) {
    const res = direction === 'RISE' || direction === 'EVEN' ? r.paid.rise : r.paid.fall;
    return res === 'REFUND' ? 'NEUTRAL' : res;
  }
  if (modality === 'EVEN_ODD') return r.digit >= 0 ? ((r.digit % 2 === 0) === (direction === 'EVEN') ? 'WIN' : 'LOSS') : 'NEUTRAL';
  if (r.move === null) return 'NEUTRAL';
  if (r.move === 'FLAT') return 'LOSS';
  return (r.move === 'UP') === (direction === 'RISE') ? 'WIN' : 'LOSS';
}

/**
 * Replays a strategy over past rounds the way SessionRunner does: one decision
 * per round reading the rounds before it, the entry settles on that round, a
 * gale goes out on the next one. Ignores meta/limit (continuous run) but
 * honours the strategy's own stops. Past results never promise future ones.
 */
export function backtestStrategy(input: BacktestInput): BacktestResult {
  const { strategy, params, modality, rounds, baseStake, progression, payoutRate } = input;
  const windowSize = input.windowSize ?? 100;
  const cycle = ROUND_CLOCKS[roundKind(modality)].cycleSeconds;
  let state = strategy.initialState(params, modality);
  let level = 0;
  let net = 0;
  let peak = 0;
  let maxDrawdown = 0;
  let lossStreak = 0;
  let maxLossStreak = 0;
  let wait = 0;
  let longestWait = 0;
  const curve = [0];
  const log: BacktestEntry[] = [];
  const res = { entries: 0, wins: 0, losses: 0, chains: 0, chainWins: 0, chainLosses: 0 };
  const winsByLevel = Array.from({ length: (progression.enabled ? progression.maxLevels : 0) + 1 }, () => 0);
  let endedBy: string | null = null;

  for (let i = 1; i < rounds.length; i++) {
    const r = rounds[i]!;
    const from = Math.max(0, i - windowSize);
    const past = rounds.slice(from, i).reverse();
    const window = {
      digits: past.map((x) => x.digit),
      quotes: [],
      moves: past.map((x) => x.move ?? 'FLAT'),
      roundIds: past.map((x) => x.id),
      // Rush: how far each round flew, like the live robot's window (candles in % need it).
      ...(past.every((x) => x.ticks !== undefined) ? { flights: past.map((x) => x.ticks!) } : {}),
    };
    // The live robot decides when the round opens for entries, one cycle before it closes.
    const tick: MarketTick = { symbol: '', epoch: r.endAt - cycle, quote: 0, pipSize: 2, digit: past[0]?.digit ?? 0 };
    const ev = strategy.onTick({ modality, params, state, window, level, roundId: r.id }, tick);
    state = ev.state;
    if (!ev.intent) {
      wait++;
      longestWait = Math.max(longestWait, wait);
      continue;
    }
    wait = 0;
    const stake = Number(stakeForLevel(baseStake, progression, level));
    const outcome = roundOutcome(modality, ev.intent.direction, r);
    const profit = outcome === 'WIN' ? Math.round(stake * payoutRate * 100) / 100 : outcome === 'LOSS' ? -stake : 0;
    res.entries++;
    if (outcome === 'WIN') res.wins++;
    else if (outcome === 'LOSS') res.losses++;
    net = Math.round((net + profit) * 100) / 100;
    peak = Math.max(peak, net);
    maxDrawdown = Math.max(maxDrawdown, Math.round((peak - net) * 100) / 100);
    curve.push(net);
    log.push({ round: r.id, at: r.endAt, direction: ev.intent.direction, level, stake, outcome, profit });

    const next = nextProgressionLevel(level, outcome, progression);
    const chainOver = next.level === 0 || next.stop;
    // A first entry that came back is no cycle: the robot reads the market again.
    if (level === 0 && outcome !== 'NEUTRAL') res.chains++;
    if (chainOver && outcome === 'WIN') {
      res.chainWins++;
      winsByLevel[level] = (winsByLevel[level] ?? 0) + 1;
      lossStreak = 0;
    } else if (chainOver && outcome === 'LOSS') {
      res.chainLosses++;
      lossStreak++;
      maxLossStreak = Math.max(maxLossStreak, lossStreak);
    }
    state = strategy.onSettlement({ modality, params, state, window }, { direction: ev.intent.direction, outcome, chainOver });
    level = next.stop ? 0 : next.level;
    endedBy = strategy.shouldEnd?.(state, params) ?? null;
    if (endedBy || next.stop) {
      endedBy ??= 'Último nível de progressão perdido';
      break;
    }
  }

  const first = rounds[0];
  const last = rounds[rounds.length - 1];
  return {
    rounds: rounds.length,
    hours: first && last ? (last.endAt - first.endAt) / 3600 : 0,
    ...res,
    winsByLevel,
    net,
    maxDrawdown,
    maxLossStreak,
    longestWait,
    curve,
    entriesLog: log,
    endedBy,
  };
}
